import {
  Copy,
  Mail,
  MapPin,
  PackageCheck,
  Phone,
  Trash2,
  Trophy,
  Truck,
  Wrench,
} from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import {
  PLANS,
  PRODUCTS,
  daysSince,
  formatDate,
  formatEuro,
  labelOf,
} from "./gulaConstants";
import { fullName, type GulaInstaller, type GulaOrder } from "./gulaTypes";
import { CustomerTrackingLine } from "./CustomerTrackingLine";
import { DeliveryStatus } from "./DeliveryStatus";
import { InstallerParteLinks } from "./InstallerParteLinks";
import { sendInstallerNotice } from "./installerNotify";
import { PacklinkDialog } from "./PacklinkDialog";
import { PacklinkPanel } from "./PacklinkPanel";
import { buildShippingText } from "./orderMessages";
import { madridDay, nextWorkingDay } from "./deliveryDates";
import { RequirementsBadge } from "./RequirementsBadge";
import { useAgents } from "./useAgents";

export type OrderAction = (
  order: GulaOrder,
  toStage: string,
  extra?: Record<string, unknown>,
) => Promise<void>;

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

export const OrderCard = ({
  order,
  installers,
  onAction,
  onConfirmReceived,
  onRefresh,
}: {
  order: GulaOrder;
  installers: GulaInstaller[];
  onAction: OrderAction;
  /** Opens the "client has the unit" confirmation */
  onConfirmReceived: (order: GulaOrder) => void;
  /** Reloads the board after a server-side change */
  onRefresh: () => void;
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { workers } = useAgents();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmNotice, setConfirmNotice] = useState(false);
  const requirementsApproved = order.requirements_status === "approved";

  const sendNotice = async () => {
    // Not blocked, but sending without the approved requirements takes a second click
    if (!requirementsApproved && !confirmNotice) {
      setConfirmNotice(true);
      return;
    }
    setConfirmNotice(false);
    setSending(true);
    try {
      const result = await sendInstallerNotice(order.id, order.installer_id);
      notify(
        `Aviso enviado a ${result.installer} (${result.to})${result.photos ? ` con ${result.photos} foto(s)` : ""}`,
        { type: "success" },
      );
      onRefresh();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido enviar",
        {
          type: "error",
        },
      );
    } finally {
      setSending(false);
    }
  };
  const [tracking, setTracking] = useState(order.tracking_number ?? "");
  const [packlinkOpen, setPacklinkOpen] = useState(false);
  const [shippingCost, setShippingCost] = useState(
    order.shipping_cost != null ? String(order.shipping_cost) : "",
  );
  const [installDate, setInstallDate] = useState(order.installation_date ?? "");
  const [deliveryDate, setDeliveryDate] = useState(
    order.expected_delivery_date ?? "",
  );
  const saveDeliveryDate = () => {
    if (deliveryDate === (order.expected_delivery_date ?? "")) return;
    onAction(order, order.stage, {
      expected_delivery_date: deliveryDate || null,
    });
  };
  const days = daysSince(order.stage_changed_at);

  const deleteOrder = async () => {
    setDeleting(true);
    try {
      await dataProvider.delete("gula_orders", {
        id: order.id,
        previousData: order,
      });
      notify(`Pedido de ${fullName(order)} borrado`, { type: "info" });
      onRefresh();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido borrar",
        { type: "error" },
      );
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const copyShipping = async () => {
    if (order.address_alert) {
      notify(
        "Antes confirmad la dirección: abre Enviar con Packlink y elige la buena",
        { type: "warning" },
      );
      return;
    }
    await navigator.clipboard.writeText(buildShippingText(order));
    notify("Datos del cliente copiados", { type: "info" });
  };

  return (
    <Card className="py-3">
      <CardContent className="flex flex-col gap-2 px-3 text-sm">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium leading-tight">{fullName(order)}</span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            {days === 0 ? "hoy" : `${days} d`}
            <button
              type="button"
              onPointerDown={stop}
              onClick={() => setConfirmDelete(true)}
              disabled={!!order.packlink_reference}
              title={
                order.packlink_reference
                  ? "Tiene envío en Packlink: cancélalo antes de borrar el pedido"
                  : "Borrar pedido (por ejemplo, si está repetido)"
              }
              className="rounded p-1 hover:bg-muted hover:text-destructive disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
        {confirmDelete ? (
          <div
            className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs"
            onPointerDown={stop}
          >
            <span>
              ¿Borrar este pedido? Se quita de Operaciones y se cierran sus
              tareas. El cliente y sus cobros de Stripe no se tocan.
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                className="h-7"
                onClick={deleteOrder}
                disabled={deleting}
              >
                {deleting ? "Borrando…" : "Sí, borrar"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                No
              </Button>
            </div>
          </div>
        ) : null}
        <span className="text-muted-foreground">
          {labelOf(PRODUCTS, order.product)}
          {order.plan && order.plan !== "none"
            ? ` + ${labelOf(PLANS, order.plan).split(" (")[0]}`
            : ""}
        </span>
        <div className="flex min-w-0 flex-wrap gap-1 [&>*]:max-w-full [&>*]:truncate">
          {order.zone ? (
            <Badge variant="secondary" className="gap-1">
              <MapPin className="h-3 w-3" />
              {order.city ? `${order.city} · ` : ""}
              {order.zone}
            </Badge>
          ) : null}
          {!order.paid_at ? (
            <Badge variant="destructive">Pendiente de pago</Badge>
          ) : null}
          {order.stage !== "installed" ? (
            <RequirementsBadge record={order} />
          ) : null}
          {order.address_alert && !order.packlink_reference ? (
            <Badge variant="destructive">Dirección por confirmar</Badge>
          ) : null}
        </div>
        {order.stage === "installed" ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Wrench className="h-3 w-3" />
            {order.installer_name ?? "Sin instalador"}
          </span>
        ) : (
          <label
            className="flex items-center gap-1 text-muted-foreground"
            onPointerDown={stop}
          >
            <Wrench className="h-3 w-3" />
            <select
              aria-label="Instalador"
              className="flex-1 rounded-md border bg-background px-1 py-0.5 text-sm text-foreground"
              value={
                order.installer_id == null ? "" : String(order.installer_id)
              }
              onChange={(event) =>
                onAction(order, order.stage, {
                  installer_id: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
            >
              <option value="">Sin instalador</option>
              {installers.map((installer) => (
                <option key={installer.id} value={String(installer.id)}>
                  {installer.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label
          className="flex items-center gap-1 text-muted-foreground"
          onPointerDown={stop}
          title="Quién ha hecho la venta: suma 10 puntos en su ranking"
        >
          <Trophy className="h-3 w-3" />
          <select
            aria-label="Vendido por"
            className={
              order.sold_by == null
                ? "flex-1 rounded-md border border-amber-300 bg-amber-50 px-1 py-0.5 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-400/10 dark:text-amber-200"
                : "flex-1 rounded-md border bg-background px-1 py-0.5 text-sm text-foreground"
            }
            value={order.sold_by == null ? "" : String(order.sold_by)}
            onChange={(event) =>
              onAction(order, order.stage, {
                sold_by: event.target.value ? Number(event.target.value) : null,
              })
            }
          >
            <option value="">Vendido por: sin asignar</option>
            {workers.map((agent) => (
              <option key={agent.id} value={String(agent.id)}>
                Vendido por {agent.first_name}
              </option>
            ))}
          </select>
        </label>
        {order.phone ? (
          <a
            href={`tel:${order.phone}`}
            onPointerDown={stop}
            className="flex items-center gap-1 text-muted-foreground hover:underline"
          >
            <Phone className="h-3 w-3" />
            {order.phone}
          </a>
        ) : null}

        {order.stage === "to_ship" ? (
          <div
            className="flex flex-col gap-2 border-t pt-2"
            onPointerDown={stop}
          >
            {order.packlink_reference ? (
              <PacklinkPanel order={order} onRefresh={onRefresh} />
            ) : (
              <Button size="sm" onClick={() => setPacklinkOpen(true)}>
                <Truck className="h-4 w-4" />
                Enviar con Packlink
              </Button>
            )}
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Envío a mano
              </summary>
              <div className="mt-2 flex flex-col gap-2">
                <Button size="sm" variant="outline" onClick={copyShipping}>
                  <Copy className="h-4 w-4" />
                  Copiar datos del cliente
                </Button>
                <Input
                  placeholder="Nº de seguimiento"
                  value={tracking}
                  onChange={(event) => setTracking(event.target.value)}
                />
                <Input
                  placeholder="Coste del envío (€ sin IVA)"
                  inputMode="decimal"
                  value={shippingCost}
                  onChange={(event) => setShippingCost(event.target.value)}
                />
                <label className="flex flex-col gap-1 text-muted-foreground">
                  Entrega prevista (orientativa)
                  <Input
                    type="date"
                    value={deliveryDate}
                    placeholder={nextWorkingDay(madridDay())}
                    onChange={(event) => setDeliveryDate(event.target.value)}
                  />
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    onAction(order, "shipped", {
                      expected_delivery_date:
                        deliveryDate || nextWorkingDay(madridDay()),
                      tracking_number: tracking || null,
                      shipping_cost:
                        shippingCost.trim() &&
                        Number.isFinite(Number(shippingCost.replace(",", ".")))
                          ? Number(shippingCost.replace(",", "."))
                          : null,
                    })
                  }
                >
                  Marcar enviado
                </Button>
              </div>
            </details>
            {packlinkOpen ? (
              <PacklinkDialog
                order={order}
                onAddressResolved={onRefresh}
                onClose={() => setPacklinkOpen(false)}
                onCreated={() => {
                  setPacklinkOpen(false);
                  onRefresh();
                }}
              />
            ) : null}
          </div>
        ) : null}

        {order.stage === "shipped" ? (
          <div
            className="flex flex-col gap-2 border-t pt-2"
            onPointerDown={stop}
          >
            <span className="text-xs text-muted-foreground">
              Enviado {formatDate(order.shipped_at)}
              {order.tracking_number ? ` · ${order.tracking_number}` : ""}
              {order.shipping_cost != null
                ? ` · envío ${formatEuro(Number(order.shipping_cost))}`
                : ""}
            </span>
            <DeliveryStatus
              order={order}
              onAction={onAction}
              onRefresh={onRefresh}
            />
            <DeliveryDayField
              value={deliveryDate}
              onChange={setDeliveryDate}
              onSave={saveDeliveryDate}
              installerName={order.installer_name}
              hasInstallerEmail={!!order.installer_email}
              isTracked={!!order.packlink_reference}
            />
            {order.packlink_reference ? (
              <PacklinkPanel order={order} onRefresh={onRefresh} />
            ) : null}
            <CustomerTrackingLine order={order} onSent={onRefresh} />
            <Button size="sm" onClick={() => onConfirmReceived(order)}>
              <PackageCheck className="h-4 w-4" />
              Cliente lo ha recibido
            </Button>
            <InstallerParteLinks order={order} />
          </div>
        ) : null}

        {order.stage === "received" ? (
          <div
            className="flex flex-col gap-2 border-t pt-2"
            onPointerDown={stop}
          >
            <DeliveryStatus
              order={order}
              onAction={onAction}
              onRefresh={onRefresh}
            />
            <span className="text-xs text-muted-foreground">
              Falta avisar al instalador
            </span>
            <Button
              size="sm"
              onClick={sendNotice}
              disabled={sending || !order.installer_email}
            >
              <Mail className="h-4 w-4" />
              {sending
                ? "Enviando…"
                : confirmNotice
                  ? "Pulsa otra vez para avisar igualmente"
                  : `Enviar aviso a ${order.installer_name ?? "instalador"}`}
            </Button>
            {confirmNotice ? (
              <span className="text-xs text-destructive">
                Los requisitos de instalación no están aprobados. El parte lo
                indicará al instalador.
              </span>
            ) : null}
            <InstallerParteLinks order={order} />
            {!order.installer_email ? (
              <span className="text-xs text-destructive">
                Falta el email del instalador (pestaña Instaladores)
              </span>
            ) : null}
          </div>
        ) : null}

        {order.stage === "installer_notified" ? (
          <div
            className="flex flex-col gap-2 border-t pt-2"
            onPointerDown={stop}
          >
            <DeliveryStatus
              order={order}
              onAction={onAction}
              onRefresh={onRefresh}
            />
            <span className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                Parte enviado a {order.installer_name ?? "instalador"}{" "}
                {formatDate(order.installer_notified_at)}
              </span>
              <button
                type="button"
                onClick={sendNotice}
                disabled={sending || !order.installer_email}
                className="underline hover:text-foreground disabled:opacity-50"
              >
                {sending
                  ? "Enviando…"
                  : confirmNotice
                    ? "Requisitos sin aprobar: pulsa otra vez"
                    : "Reenviar aviso"}
              </button>
            </span>
            <InstallerParteLinks order={order} />
            <Input
              type="date"
              value={installDate}
              onChange={(event) => setInstallDate(event.target.value)}
              onBlur={() =>
                installDate !== (order.installation_date ?? "") &&
                onAction(order, "installer_notified", {
                  installation_date: installDate || null,
                })
              }
            />
            <Button size="sm" onClick={() => onAction(order, "installed")}>
              <Wrench className="h-4 w-4" />
              Marcar instalado
            </Button>
          </div>
        ) : null}

        {order.stage === "installed" ? (
          <span className="text-xs text-muted-foreground">
            Instalado {formatDate(order.installed_at)}
          </span>
        ) : null}
      </CardContent>
    </Card>
  );
};

/** Expected delivery day on a shipped order, and when the installer gets the parte. */
const DeliveryDayField = ({
  value,
  onChange,
  onSave,
  installerName,
  hasInstallerEmail,
  isTracked,
}: {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  installerName?: string | null;
  hasInstallerEmail: boolean;
  /** Packlink shipment: the delivery is confirmed automatically */
  isTracked: boolean;
}) => {
  return (
    <div className="flex flex-col gap-1 rounded-md bg-lime-50 p-2 text-xs dark:bg-lime-950/30">
      <label className="flex items-center justify-between gap-2 font-medium">
        Entrega prevista
        <Input
          type="date"
          className="h-7 w-auto text-xs"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onSave}
        />
      </label>
      <span className="text-muted-foreground">
        {!installerName || !hasInstallerEmail
          ? "Falta el instalador o su email: el parte no podrá salir solo."
          : isTracked
            ? `El parte a ${installerName} sale solo cuando Packlink confirme la entrega.`
            : `Sin seguimiento automático: cuando el cliente confirme que lo tiene, pulsa «Cliente lo ha recibido» y se avisa a ${installerName}.`}
      </span>
    </div>
  );
};
